import axios, { AxiosError } from "axios";
import React, { FormEvent, useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CreateMagasiEnligne from "../../Views/Utilisateure/visiteur/CreateMagasiEnligne";

/* ─────────────────────────────────────────────────────────
   OTP Step — inline component
───────────────────────────────────────────────────────── */
function OtpStep({
  email,
  onVerified,
  onBack,
}: {
  email: string;
  onVerified: () => void;
  onBack: () => void;
}) {
  const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  const handleChange = (val: string, idx: number) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...code];
    next[idx] = val.slice(-1);
    setCode(next);
    if (val && idx < 5) inputRefs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent, idx: number) => {
    if (e.key === "Backspace" && !code[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(""));
      inputRefs.current[5]?.focus();
    }
  };

  const verify = async () => {
    const fullCode = code.join("");
    if (fullCode.length < 6) {
      setError("Veuillez entrer les 6 chiffres.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("email", email);
      fd.append("code", fullCode);
      const { data } = await axios.post(
        `${process.env.REACT_APP_PHP_APP_URL}/verify-email-code`,
        fd
      );
      if (data.valid) {
        onVerified();
      } else {
        setError("Code incorrect ou expiré. Vérifiez et réessayez.");
        setCode(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    } catch {
      setError("Erreur de connexion. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (countdown > 0) return;
    setResending(true);
    try {
      const fd = new FormData();
      fd.append("email", email);
      fd.append("name", "Propriétaire");
      await axios.post(
        `${process.env.REACT_APP_PHP_APP_URL}/send-verification-code`,
        fd
      );
      setCountdown(60);
      setCode(["", "", "", "", "", ""]);
      setError("");
      inputRefs.current[0]?.focus();
    } catch {
      setError("Impossible de renvoyer le code.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div style={st.wrapper}>
      <div style={st.card}>
        <button style={st.backBtn} onClick={onBack}>← Retour</button>

        <div style={st.iconWrap}>📧</div>
        <h2 style={st.title}>Vérification de l'email</h2>
        <p style={st.sub}>
          Nous avons envoyé un code à 6 chiffres à<br />
          <strong style={{ color: "#d97706" }}>{email}</strong>
        </p>

        <div style={st.otpRow} onPaste={handlePaste}>
          {code.map((digit, i) => (
            <input
              key={i}
              ref={(el) => (inputRefs.current[i] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(e.target.value, i)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              style={{
                ...st.otpBox,
                borderColor: digit ? "#f59e0b" : "#d1d5db",
                boxShadow: digit ? "0 0 0 3px rgba(245,158,11,.18)" : "none",
              }}
              autoFocus={i === 0}
            />
          ))}
        </div>

        {error && <p style={st.errTxt}>⚠️ {error}</p>}

        <button
          style={{ ...st.verifyBtn, opacity: loading ? 0.7 : 1, cursor: loading ? "not-allowed" : "pointer" }}
          onClick={verify}
          disabled={loading}
        >
          {loading ? "Vérification…" : "Confirmer le code"}
        </button>

        <p style={st.resendRow}>
          Pas reçu ?{" "}
          <button
            style={{ ...st.resendBtn, opacity: countdown > 0 ? 0.5 : 1, cursor: countdown > 0 ? "not-allowed" : "pointer" }}
            onClick={resend}
            disabled={countdown > 0 || resending}
          >
            {resending ? "Envoi…" : countdown > 0 ? `Renvoyer (${countdown}s)` : "Renvoyer"}
          </button>
        </p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Success Screen — waiting admin activation
───────────────────────────────────────────────────────── */
function PendingScreen() {
  const navigate = useNavigate();
  const [countdown, setCountdown] = React.useState(5);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(interval);
          navigate("/login");
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [navigate]);

  return (
    <div style={st.wrapper}>
      <div style={{ ...st.card, textAlign: "center" as const }}>
        <div style={{ fontSize: "56px", marginBottom: "16px" }}>🔐</div>
        <h2 style={st.title}>Demande envoyée !</h2>
        <p style={st.sub}>
          Votre magasin a été créé avec succès.
          <br /><br />
          <strong>Vous recevrez un message dans votre email</strong> lorsque
          votre compte sera activé par notre équipe d'administration.
        </p>
        <div style={st.infoBox}>
          <span style={{ fontSize: "20px" }}>📩</span>
          <span style={{ fontSize: "0.9rem", color: "#92400e", fontWeight: 500 }}>
            Gardez un œil sur votre boîte email — un message vous sera envoyé dès
            l'activation de votre compte.
          </span>
        </div>
        <p style={{ ...st.sub, color: "#d97706", fontWeight: 600 }}>
          Redirection dans {countdown}s…
        </p>
        <button style={st.verifyBtn} onClick={() => navigate("/connecter_compte")}>
          Continuer vers la connexion
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Main Service Component
───────────────────────────────────────────────────────── */
export default function AddMagasinenligne() {
  const navigate = useNavigate();

  const [NomMagasin, setNomMagasin]         = useState<string>("");
  const [ReceptionClois, setReceptionClois] = useState<boolean>(false);
  const [Latitude, setLatitude]             = useState<any>(0);
  const [Longitude, setLongitude]           = useState<any>(0);
  const [nom, setnom]                       = useState<string>("");
  const [email, setemail]                   = useState<string>("");
  const [password, setpassword]             = useState<string>("");
  const [tele, settele]                     = useState<string>("");
  const [adresse, setAdresse]               = useState<string>("");
  const [messageError, setMessageError]     = useState<string>("");
  const [messageSucess, setMessageSucess]   = useState<string>("");

  // OTP step: "form" | "otp" | "creating" | "pending"
  const [step, setStep] = useState<"form" | "otp" | "creating" | "pending">("form");
  const [sendingOtp, setSendingOtp] = useState(false);

  /* ── Step 1: Validate & send OTP ── */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!nom.trim() || !NomMagasin.trim() || !email.trim() || !password.trim() || !tele.trim()) {
      setMessageError("Veuillez remplir tous les champs obligatoires.");
      setTimeout(() => setMessageError(""), 3000);
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setMessageError("Adresse email invalide.");
      setTimeout(() => setMessageError(""), 3000);
      return;
    }

    // Send OTP to email
    setSendingOtp(true);
    try {
      const fd = new FormData();
      fd.append("email", email);
      fd.append("name", nom);
      await axios.post(
        `${process.env.REACT_APP_PHP_APP_URL}/send-verification-code`,
        fd
      );
      setStep("otp");
    } catch {
      setMessageError("Impossible d'envoyer le code de vérification. Réessayez.");
      setTimeout(() => setMessageError(""), 3000);
    } finally {
      setSendingOtp(false);
    }
  };

  /* ── Step 2 → 3: OTP verified → create magasin ── */
  const onOtpVerified = async () => {
    const formData = new FormData();
    formData.append("NomMagasin",    NomMagasin);
    formData.append("nom",           nom);
    formData.append("email",         email);
    formData.append("password",      password);
    formData.append("tele",          tele);
    formData.append("latitude",      Latitude);
    formData.append("longitude",     Longitude);
    formData.append("adress",        adresse);
    formData.append("ReceptionClois", ReceptionClois ? "1" : "0");

    setStep("creating"); // show loader
    try {
      await axios.post(
        `${process.env.REACT_APP_PHP_APP_URL}/magasin-enligne/create`,
        formData
      );
      setStep("pending");
    } catch (error: AxiosError | any) {
      setStep("form");
      setMessageError("Veuillez saisir toutes les informations correctement.");
      setTimeout(() => setMessageError(""), 3000);
    }
  };

  /* ── Render ── */
  if (step === "creating") {
    return (
      <div style={st.wrapper}>
        <div style={{ ...st.card, textAlign: "center" as const }}>
          <div style={magasinSpinnerStyle.spinner} />
          <h2 style={{ ...st.title, marginTop: "24px" }}>Envoi de la demande…</h2>
          <p style={st.sub}>Veuillez patienter, votre demande est en cours de traitement.</p>
        </div>
      </div>
    );
  }

  if (step === "otp") {
    return (
      <OtpStep
        email={email}
        onVerified={onOtpVerified}
        onBack={() => setStep("form")}
      />
    );
  }

  if (step === "pending") {
    return <PendingScreen />;
  }

  return (
    <CreateMagasiEnligne
      adress={adresse}
      setAdress={setAdresse}
      Latitude={Latitude}
      setLatitude={setLatitude}
      Longitude={Longitude}
      setLongitude={setLongitude}
      NomMagasin={NomMagasin}
      setNomMagasin={setNomMagasin}
      nom={nom}
      setnom={setnom}
      email={email}
      setemail={setemail}
      password={password}
      setpassword={setpassword}
      tele={tele}
      settele={settele}
      ReceptionClois={ReceptionClois}
      setReceptionClois={setReceptionClois}
      messageError={messageError}
      setMessageError={setMessageError}
      messageSucess={messageSucess}
      setMessageSucess={setMessageSucess}
      handleSubmit={handleSubmit}
      sendingOtp={sendingOtp}
    />
  );
}

/* ─────────────────────────────────────────────────────────
   Shared inline styles
───────────────────────────────────────────────────────── */
const st: Record<string, React.CSSProperties> = {
  wrapper: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
    padding: "24px 16px",
    fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  card: {
    background: "#fff",
    borderRadius: "20px",
    padding: "48px 44px",
    width: "100%",
    maxWidth: "480px",
    boxShadow: "0 20px 60px rgba(217,119,6,.15)",
    border: "1px solid rgba(245,158,11,.2)",
  },
  backBtn: {
    background: "none",
    border: "none",
    color: "#d97706",
    cursor: "pointer",
    fontSize: "0.9rem",
    fontWeight: 500,
    padding: 0,
    marginBottom: "24px",
    display: "block",
  },
  iconWrap: {
    fontSize: "48px",
    textAlign: "center",
    display: "block",
    marginBottom: "12px",
  },
  title: {
    textAlign: "center",
    fontSize: "1.6rem",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: "0 0 10px",
  },
  sub: {
    textAlign: "center",
    color: "#6b7280",
    fontSize: "0.92rem",
    lineHeight: 1.6,
    margin: "0 0 28px",
  },
  infoBox: {
    display: "flex",
    gap: "12px",
    alignItems: "flex-start",
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "24px",
  },
  otpRow: {
    display: "flex",
    gap: "10px",
    justifyContent: "center",
    marginBottom: "20px",
  },
  otpBox: {
    width: "52px",
    height: "58px",
    textAlign: "center",
    fontSize: "1.5rem",
    fontWeight: 700,
    border: "2px solid #d1d5db",
    borderRadius: "12px",
    color: "#1a1a1a",
    outline: "none",
    transition: "border-color .2s, box-shadow .2s",
  },
  errTxt: {
    textAlign: "center",
    color: "#ef4444",
    fontSize: "0.85rem",
    margin: "0 0 16px",
  },
  verifyBtn: {
    width: "100%",
    padding: "14px",
    background: "linear-gradient(135deg, #f59e0b, #d97706)",
    border: "none",
    borderRadius: "12px",
    color: "#1a1a1a",
    fontSize: "1rem",
    fontWeight: 700,
    cursor: "pointer",
    transition: "opacity .2s",
    marginBottom: "16px",
  },
  resendRow: {
    textAlign: "center",
    fontSize: "0.85rem",
    color: "#6b7280",
    margin: 0,
  },
  resendBtn: {
    background: "none",
    border: "none",
    color: "#d97706",
    fontWeight: 600,
    cursor: "pointer",
    fontSize: "0.85rem",
    padding: 0,
  },
};

/* Spinner for the "creating" loader step */
if (typeof document !== "undefined" && !document.getElementById("wyn-mag-spin-style")) {
  const styleEl = document.createElement("style");
  styleEl.id = "wyn-mag-spin-style";
  styleEl.textContent = "@keyframes mag-spin { to { transform: rotate(360deg); } }";
  document.head.appendChild(styleEl);
}

const magasinSpinnerStyle: Record<string, React.CSSProperties> = {
  spinner: {
    width: "64px",
    height: "64px",
    border: "6px solid #fde68a",
    borderTop: "6px solid #d97706",
    borderRadius: "50%",
    animation: "mag-spin 0.8s linear infinite",
    margin: "0 auto",
  },
};